import { Component, OnInit } from '@angular/core';
import { CampagnesService } from '../../services/requests/campagnes.service';
import { UsersService } from "../../services/requests/users.service";

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-archive',
  templateUrl: './archive.component.html',
  styleUrls: ['./archive.component.css']
})
export class ArchiveComponent implements OnInit {
  loading = true;
  clients: Array<any> = [];
  campagnes: Array<any> = [];
  filteredCampagnes: Array<any> = [];
  searchTerm: string = '';
  sortClientAsc = true;
  isAdmin = false;
  isSousAdmin = false;
  constructor(private campagnesService: CampagnesService,
              private usersService: UsersService) { }

  ngOnInit(): void {
    this.isAdmin = localStorage.getItem("isAdmin") === "true";
    this.isSousAdmin = localStorage.getItem("isSousAdmin") === "true";
    this.getCampagnes();
    this.getClients();
  }
  updateConsiderArchive(id: string): void {
    const campagne = this.campagnes.find(c => c._id === id);
    if (!campagne) {
      return;
    }

    const considererArchive = !campagne.considererArchive;
    campagne.considererArchive = considererArchive;

    this.campagnesService.updateConsiderArchive({ id, considererArchive }).subscribe({
      next: (response: any) => {
        if (!response.status) {
          campagne.considererArchive = !considererArchive;
          showNotif('Erreur lors de la mise à jour', 'error');
        } else {
          showNotif('Modifié avec succès', 'success');
        }
      },
      error: () => {
        campagne.considererArchive = !considererArchive;
        showNotif('Erreur lors de la mise à jour', 'error');
      }
    });
  }
  getClients(): void {
    this.usersService.getAll().subscribe({
      next: (res: any) => {
        if (res.status && res.data?.users) {
          this.clients = res.data.users.filter((u: any) => u.typeAccount === 'client');
          this.applyFilter();
        }
      },
      error: (err) => {
        console.error("❌ Erreur getClients() :", err);
      }
    });
  }
  getCampagnes(): void {
    this.loading = true;
    
    this.campagnesService.getArchives().subscribe({
      next: (res) => {
        if (res.status && Array.isArray(res.data)) {
          this.campagnes = res.data.map((campagne: any) => this.formatCampagne(campagne));
        } else {
          this.campagnes = [];
        }
        this.applyFilter();
        this.loading = false;
      },
      error: (err) => {
        console.error("❌ Erreur getArchives() :", err);
        this.campagnes = [];
        this.applyFilter();
        this.loading = false;
      }
    });
  }
  
  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    
    if (!term) {
      this.filteredCampagnes = [...this.campagnes];
    } else {
      this.filteredCampagnes = this.campagnes.filter(c => {
        const clientId = this.getClientIdentifiant(c.idClient).toLowerCase();
        const nom = c.nomCampagne?.toLowerCase() || '';
        const msg = c.message?.toLowerCase() || '';
        return clientId.includes(term) || nom.includes(term) || msg.includes(term);
      });
    }
    
    this.sortByClient();
  }

  toggleSortClient(): void {
    this.sortClientAsc = !this.sortClientAsc;
    this.sortByClient();
  }

  sortByClient(): void {
    this.filteredCampagnes.sort((a, b) => {
      const clientA = this.getClientIdentifiant(a.idClient).toLowerCase();
      const clientB = this.getClientIdentifiant(b.idClient).toLowerCase();
      return this.sortClientAsc 
        ? clientA.localeCompare(clientB) 
        : clientB.localeCompare(clientA);
    });
  }
  supprimer(id: string): void {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet envoi ?')) {
      return;
    }

    this.loading = true;
    this.campagnesService.delete({ id }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.status) {
          this.getCampagnes();
          showNotif('Campagne supprimée avec succès', 'success');
        } else {
          showNotif('Erreur lors de la suppression', 'error');
        }
      },
      error: () => {
        this.loading = false;
        showNotif('Erreur lors de la suppression', 'error');
      }
    });
  }

  relancerFailed(id: string): void {
    this.loading = true;
    this.campagnesService.relancerFailed({ id, type: 'campagne' }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.status) {
          showNotif('Failed relancé avec succès', 'success');
        } else {
          showNotif('Erreur lors de la relance', 'error');
        }
      },
      error: () => {
        this.loading = false;
        showNotif('Erreur lors de la relance', 'error');
      }
    });
  }

  getClientIdentifiant(id: string): string {
    const client = this.clients.find(cc => cc._id === id);
    return client ? client.identifiant : '-';
  }

  getPercentage(campagne: any): number {
    if (!campagne?.total) return 0;
    const total = campagne.total;
    const reallySend = campagne.reallySend || campagne.envoyer || 0;
    const failed = campagne.failed || 0;
    return Math.round(((reallySend + failed) / total) * 100);
  }

  getFailedPercentage(campagne: any): number {
    if (!campagne) return 0;
    const reallySend = campagne.reallySend || campagne.envoyer || 0;
    const failed = campagne.failed || 0;
    const total = reallySend + failed;
    if (total === 0) return 0;
    return Math.round((failed / total) * 100);
  }

  exporter(dossier: string, id: string): void {
    this.loading = true;
    this.campagnesService.downloadExportFile({
      idCampaign: id,
      dossier,
      type: 'campagne'
    }).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (!response.status || !response.data?.url) {
          showNotif('Erreur lors de l\'export du fichier', 'error');
          return;
        }
        window.location.href = response.data.url;
      },
      error: () => {
        this.loading = false;
        showNotif('Erreur lors de l\'export du fichier', 'error');
      }
    });
  }

  private formatCampagne(campagne: any): any {
    return {
      ...campagne,
      total: campagne?.total || 0,
      nbAncienBlacklist: campagne?.nbAncienBlacklist || 0,
      nombreDuplicate: campagne?.nombreDuplicate || 0,
      nombreTooLong: campagne?.nombreTooLong || 0,
      nombreStop: campagne?.nombreStop || 0,
      nombreReception: campagne?.nombreReception || 0,
      envoyer: campagne?.envoyer || 0,
      reallySend: campagne?.reallySend || campagne?.envoyer || 0,
      enAttente: campagne?.enAttente || 0,
      failed: campagne?.failed || 0,
      affected: campagne?.affected || 0,
      notAffected: campagne?.notAffected || 0,
      considererArchive: campagne?.considererArchive || false
    };
  }
  
  formatDateCampagne(campagne: any): string {
    if (!campagne) return '';

    const now = Date.now();
    const scheduleDate = new Date(campagne.schedule);
    const dateTimestamp = Number(campagne.date);

    const formatShort = (timestamp: number): string =>
      new Date(timestamp).toLocaleString('fr-FR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });

    if (campagne.schedule && !isNaN(scheduleDate.getTime())) {
      const scheduleTimestamp = scheduleDate.getTime();
      if (scheduleTimestamp > now + 60000) {
        return `📅 Prévue : ${formatShort(scheduleTimestamp)}`;
      }
    }

    if (dateTimestamp && !isNaN(dateTimestamp)) {
      return `⏱️ Lancée : ${formatShort(dateTimestamp)}`;
    }

    if (campagne.schedule) {
      const scheduleTimestamp = new Date(campagne.schedule).getTime();
      if (!isNaN(scheduleTimestamp)) {
        return formatShort(scheduleTimestamp);
      }
    }

    return '';
  }
  
  copyRefCampagne(text: string, event: Event): void {
    event.stopPropagation();
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        showNotif('📋 ID copié dans le presse-papiers', 'success');
      }).catch(() => {
        showNotif('Erreur lors de la copie dans le presse-papiers', 'error');
      });
    } else {
      showNotif('La copie dans le presse-papiers n\'est pas disponible dans ce navigateur', 'error');
    }
  }
}